import _ from "lodash";
import React, { useMemo, useState } from "react";
import classes from "./ReportItem.scss";
import {
  LockOpenOutlined,
  WarningCircleOutlined,
  TickLargeOutlined,
  LockOutlined,
  TickCircleOutlined,
  CloseCircleOutlined,
} from "@toddle-edu/ds-icons";
import { Button, IconButton } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { useI18n } from "Hooks";

import {
  SUBMISSION_PENDING,
  CAN_MANAGE_IA_EA_PERMISSION,
} from "Projects/Constants/stringConstants";
import {
  getProjectDeadlinesDetailsText,
  getIsUploadInProgress,
} from "Projects/modules/Utils";
import {
  getSubmissionStatusLabelDetails,
  isIEAssessment,
} from "Projects/modules/ProjectModules";

import { submissionTypeTitle } from "../../modules/Utils";
import ReportSubmissionItem from "../ReportSubmissionItem";
import { showSubmissionItem } from "Projects/routes/DeadlinesV2/modules/Utils";
import ACLStorage from "lib/aclService";

const TEST_ID_PREFIX = "projects-reportItem";

const ReportItem = props => {
  const {
    projectTask: {
      isComplete: isDeadlineComplete,
      latestSubmissionResponse,
      id: projectTaskId,
      task: projectGroupTask,
    },
    onUpdateProjectDeadline,
    onUpdateProjectDeadlineSubmission,
    projectGroupSubType,
    onDeleteAttachment,
    onUpdateAttachment,
    onCreateAttachment,
    onAttachmentClick,
    projectDisabled,
    onUpdateInputField,
    isUserStudentOrParent,
    projectId,
    autoCheckSimilarityRef,
    progressOfUploads,
  } = props;

  const { t } = useI18n(["common", "project"]);

  const [showPdfTronEditButton, setShowPdfTronEditButton] = useState(false);

  const onEditAttachmentClick = () => {
    setShowPdfTronEditButton(true);
  };

  const togglePdfTronEditButton = value => {
    setShowPdfTronEditButton(value);
  };
  const {
    itemResponses,
    isLocked: isSubmissionLocked,
    status: submissionStatus,
    id: submissionId,
    isTurnedIn: isSubmissionTurnedIn,
    isReturned: isSubmissionReturned,
    submission,
  } = latestSubmissionResponse;

  const isUploadInProgress = getIsUploadInProgress({
    progressOfUploads,
    itemResponses,
  });
  const { items: projectGroupSubmissionItems } = submission[0];

  const {
    title: projectGroupTaskTitle,
    dueOn,
    closesOn,
    submission: { title: submissionTitle, type: submissionType },
    isDeadlineAllDay,
  } = projectGroupTask;
  const disabled =
    projectDisabled ||
    isSubmissionLocked ||
    (isUserStudentOrParent && isSubmissionTurnedIn);

  const { dueDateSubtext, closeDateSubtext } = getProjectDeadlinesDetailsText({
    dueOn,
    closesOn,
    isDeadlineAllDay,
    t,
  });

  const projectGroupsSubmissionFieldUID = _.get(submission, "[0].fieldUID", {});
  const projectGroupSubmissionFieldConfig = _.get(
    submission,
    "[0].fieldConfig",
    {}
  )?.[projectGroupsSubmissionFieldUID];

  const SubmissionLockIconStatus = () => {
    return isSubmissionLocked ? (
      <div className={classes.statusContainer}>
        <LockOutlined size="xxx-small" variant="subtle" />
        <span className="text-body-s">{t("project:submission_closed")}</span>
      </div>
    ) : (
      <div className={classes.statusContainer}>
        <LockOpenOutlined size="xxx-small" variant="subtle" />
        <span className="text-body-s">{t("project:submission_open")}</span>
      </div>
    );
  };

  const DeadlineStatus = () => {
    return isDeadlineComplete ? (
      <div className={classes.statusContainer}>
        <TickLargeOutlined size="xxx-small" variant="subtle" />
        <span className="text-body-s">{t("common:completed")}</span>
      </div>
    ) : (
      <div className={classes.statusContainer}>
        <WarningCircleOutlined size="xxx-small" variant="subtle" />
        <span className="text-body-s">{t("common:pending")}</span>
      </div>
    );
  };

  const SubmissionStatus = () => {
    const { label, textColor, icon } = getSubmissionStatusLabelDetails({
      status: submissionStatus,
    });
    return (
      !_.includes([SUBMISSION_PENDING], submissionStatus) && (
        <>
          <span className={classes.dot}></span>
          <div className={classes.statusContainer}>
            {icon}
            <span className="text-body-s" style={{ color: textColor }}>
              {t(label)}
            </span>
          </div>
        </>
      )
    );
  };

  const {
    iconVariant: deadlineButtonIconVariant,
    buttonLabel: deadlineButtonLabel,
    buttonType: deadlineButtonType,
    icon: deadlineIcon,
  } = useMemo(() => {
    if (!isDeadlineComplete) {
      return {
        iconVariant: "progressive",
        buttonType: "fill",
        buttonLabel: t("project:mark_as_complete"),
        icon: <TickCircleOutlined />,
      };
    } else {
      return {
        iconVariant: "neutral",
        buttonType: "outlined",
        buttonLabel: t("project:mark_as_pending"),
        icon: <CloseCircleOutlined />,
      };
    }
  }, [isDeadlineComplete]);

  const getActionsIcons = () => {
    if (isUserStudentOrParent) {
      if (!projectDisabled && !isSubmissionLocked) {
        return isSubmissionTurnedIn ? (
          <div>
            <Button
              variant="neutral"
              type="outlined"
              icon={<CloseCircleOutlined />}
              isFullWidth={true}
              onClick={() => {
                onUpdateProjectDeadlineSubmission({
                  input: {
                    submissionIds: [submissionId],
                    propertiesToUpdate: {
                      isTurnedIn: false,
                    },
                  },
                });
              }}
              testId={`${TEST_ID_PREFIX}-unsubmit-${submissionId}`}
            >
              {t("common:unsubmit")}
            </Button>
          </div>
        ) : (
          <div>
            <Button
              type="fill"
              icon={<TickCircleOutlined />}
              variant="progressive"
              isFullWidth={true}
              disabled={isUploadInProgress}
              onClick={() => {
                onUpdateProjectDeadlineSubmission({
                  input: {
                    submissionIds: [submissionId],
                    propertiesToUpdate: {
                      isTurnedIn: true,
                      isReturned: false,
                    },
                  },
                });
              }}
              testId={`${TEST_ID_PREFIX}-submit-${submissionId}`}
            >
              {t("common:mark_as_done")}
            </Button>
          </div>
        );
      }
    } else {
      const isProjectGroupIEAssessment = isIEAssessment({
        projectGroupSubType,
      });

      const canManageIAEA = isProjectGroupIEAssessment
        ? ACLStorage.getPermissionValue(CAN_MANAGE_IA_EA_PERMISSION)
        : true;

      if (!canManageIAEA) {
        return null;
      }

      return (
        <>
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            icon={isSubmissionLocked ? <LockOutlined /> : <LockOpenOutlined />}
            disabled={isUploadInProgress}
            onClick={() => {
              onUpdateProjectDeadlineSubmission({
                input: {
                  submissionIds: [submissionId],
                  propertiesToUpdate: {
                    isLocked: !isSubmissionLocked,
                  },
                },
              });
            }}
            testId={`${TEST_ID_PREFIX}-toggleLock-${submissionId}`}
          />
          <Button
            dsVersion="2.0"
            variant={deadlineButtonIconVariant}
            type={deadlineButtonType}
            icon={deadlineIcon}
            disabled={isUploadInProgress}
            onClick={() => {
              onUpdateProjectDeadline({
                input: {
                  deadlineIds: [projectTaskId],
                  isComplete: !isDeadlineComplete,
                },
              });
            }}
            testId={`${TEST_ID_PREFIX}-toggleDeadlineStatus-${projectTaskId}`}
          >
            {deadlineButtonLabel}
          </Button>
        </>
      );
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className="text-label-l">
          {t(submissionTypeTitle[submissionTitle])}
          {/** jatin should we update it to using type instead of title */}
        </div>
        <div className={classes.rightContainer}>
          <div
            className={classNames({
              [classes.dateAndStatusContainer]: true,
              [classes.marginRight]: !(disabled && isUserStudentOrParent),
            })}
          >
            <div className={classes.dateContainer}>
              <span className="text-body-s">{dueDateSubtext}</span>
              <span className={classes.dot}></span>
              <span className="text-body-s">{closeDateSubtext}</span>
            </div>
            <div className={classes.statusContainer}>
              <SubmissionLockIconStatus />
              <span className={classes.dot}></span>
              <DeadlineStatus />

              <SubmissionStatus />
            </div>
          </div>
          {getActionsIcons()}
        </div>
      </div>
      {_.map(itemResponses, itemResponse => {
        const {
          submissionItem: { id: itemId },
        } = itemResponse;

        const submissionItemFieldConfig = _.find(projectGroupSubmissionItems, {
          id: itemId,
        })?.fieldConfig;

        const {
          submissionItem: { fieldUID },
        } = itemResponse;
        const fieldDetails = submissionItemFieldConfig[fieldUID];
        const fieldConfig = fieldDetails?.config;
        const otherFieldDetails = {
          fieldType: fieldDetails?.type,
        };

        const {
          hasDynamicSubmissionItems,
          config: { childrenFieldUIds },
        } = projectGroupSubmissionFieldConfig;

        const showItem = showSubmissionItem({
          hasDynamicSubmissionItems,
          fieldUIDs: _.get(childrenFieldUIds, submissionType, []),
          itemFieldUID: fieldUID,
        });

        return showItem ? (
          <ReportSubmissionItem
            onUpdateInputField={onUpdateInputField}
            onUpdateAttachment={onUpdateAttachment}
            onCreateAttachment={onCreateAttachment}
            disabled={disabled}
            itemResponse={itemResponse}
            submissionId={submissionId}
            t={t}
            onAttachmentClick={onAttachmentClick}
            onDeleteAttachment={onDeleteAttachment}
            isUserStudentOrParent={isUserStudentOrParent}
            projectId={projectId}
            {...fieldConfig}
            {...otherFieldDetails}
            autoCheckSimilarityRef={autoCheckSimilarityRef}
            onEditAttachmentClick={onEditAttachmentClick}
            showPdfTronEditButton={showPdfTronEditButton}
            togglePdfTronEditButton={togglePdfTronEditButton}
          />
        ) : null;
      })}
    </div>
  );
};

export default ReportItem;
